import { useMutation, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { USE_GET_TRAINING_TYPES_QUERY_KEY } from '@/api/trainers'
import { composeFormData } from '@/utils'

import { useInvalidateServices } from './get-services'
import { Service } from './services.types'

export interface ServiceFields {
  active: boolean
  activeType: boolean
  bookingType?: number | ''
  category: string
  color: string
  description: string
  icon: string
  image?: File
  imageInCategory?: File
  internalName: string
  name: string
  personal: boolean
}

export interface CreateServiceRequest extends ServiceFields {
  duplicateId?: number
}

export interface UpdateServiceRequest extends ServiceFields {
  id: number
}

// Note: BE reads the images with req.file(...), so a service is always sent as multipart
const MULTIPART = { headers: { 'content-type': 'multipart/form-data' } }

export const createService = async (data: CreateServiceRequest) => {
  const response = await authAPI.post<Service>('/shop', composeFormData({ ...data }), MULTIPART)
  return response.data
}

export const updateService = async ({ id, ...data }: UpdateServiceRequest) => {
  const response = await authAPI.put<Service>(
    `/shop/${id}`,
    composeFormData({ ...data }),
    MULTIPART,
  )
  return response.data
}

export const deleteService = async (id: number) => {
  await authAPI.delete(`/shop/${id}`)
}

// Note: a service rename / flag change is mirrored onto its training types by the BE
const useInvalidateServicesAndTrainingTypes = () => {
  const queryClient = useQueryClient()
  const invalidateServices = useInvalidateServices()

  return () =>
    Promise.all([
      invalidateServices(),
      queryClient.invalidateQueries({ queryKey: [USE_GET_TRAINING_TYPES_QUERY_KEY] }),
    ])
}

export const useCreateService = () => {
  const invalidate = useInvalidateServicesAndTrainingTypes()
  return useMutation({ mutationFn: createService, onSuccess: invalidate })
}

export const useUpdateService = () => {
  const invalidate = useInvalidateServicesAndTrainingTypes()
  return useMutation({ mutationFn: updateService, onSuccess: invalidate })
}

export const useDeleteService = () => {
  const invalidate = useInvalidateServicesAndTrainingTypes()
  return useMutation({ mutationFn: deleteService, onSuccess: invalidate })
}
